export const API_BASE_URL = process.env.REACT_APP_API_URL;

class ApiService {
  async getUsers() {
    const res = await fetch(`${API_BASE_URL}/admin/users`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  }

  async updateUser(userId, data) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update user');
    return res.json();
  }

  async createUser(data) {
    const res = await fetch(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => null);
      throw new Error(errorData?.error || 'Failed to create user');
    }
    return res.json();
  }

  // Create an order on behalf of a user
  async createAdminOrder(userId, data) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || 'Failed to create order');
    }
    return res.json();
  }

  // Admin "take order" wizard config (units, VAT rates, shipping tiers)
  async getAdminOrderConfig() {
    const res = await fetch(`${API_BASE_URL}/admin/order-config`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch order config');
    return res.json();
  }

  // Admin "take order" wizard — applicable offers + volume discount for a draft
  async evaluateAdminOrderOffers(userId, data) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/order-offers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || 'Failed to evaluate offers');
    }
    return res.json();
  }

  // Get addresses for a specific user (Admin)
  async getAdminUserAddresses(userId) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/addresses`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch user addresses');
    return res.json();
  }

  // Get billing addresses for a specific user (Admin)
  async getAdminUserBillingAddresses(userId) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/billing-addresses`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch user billing addresses');
    return res.json();
  }

  // Add shipping address for a user (Admin)
  async addAdminShippingAddress(userId, data) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/addresses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to add shipping address');
    }
    return res.json();
  }

  // Add billing address for a user (Admin)
  async addAdminBillingAddress(userId, data) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/billing-addresses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to add billing address');
    }
    return res.json();
  }

  // Set default shipping address (Admin)
  async setAdminShippingDefault(userId, addressId) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/addresses/${addressId}/default`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to update address');
    return res.json();
  }

  // Set default billing address (Admin)
  async setAdminBillingDefault(userId, addressId) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/billing-addresses/${addressId}/default`, {
      method: 'PUT',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to update address');
    return res.json();
  }

  // List monthly-invoice billing-address groups for a user + month
  async getMonthlyInvoiceGroups(userId, month) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/monthly-invoice-groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ month })
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to load monthly invoice groups');
    }
    return data;
  }

  // Preview monthly invoice (PDF + summary, no persist/email)
  // billingAddressId: null -> the "no billing address" group for this user.
  async previewMonthlyInvoice(userId, lang = 'en', month, billingAddressId = null) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/preview-monthly-invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lang, month, billingAddressId })
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to preview monthly invoice');
    }
    return data;
  }

  // Issue monthly invoice using a previously generated preview
  async issueMonthlyInvoice(userId, previewKey) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/issue-monthly-invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ previewKey })
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to issue monthly invoice');
    }
    return data;
  }

  async fulfillOrder(orderId, payload) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/fulfill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to fulfill order');
    return data;
  }

  async previewFulfillment(orderId, fulfillmentItems, note) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/fulfill-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ fulfillmentItems, note })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to generate preview');
    }
    return res.blob();
  }

  async saveFulfillDraft(orderId, payload) {
    return this.fulfillOrder(orderId, { ...payload, action: 'save_draft' });
  }

  async updateFulfillDraft(fulfillmentId, payload) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/fulfillments/${fulfillmentId}/draft`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update draft fulfillment');
    return data;
  }

  async reopenFulfillment(fulfillmentId) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/fulfillments/${fulfillmentId}/reopen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({})
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to reopen fulfillment');
    return data;
  }

  async getFulfillmentSubstitutes(lang) {
    const res = await fetch(`${API_BASE_URL}/admin/fulfillment/substitutes?lang=${encodeURIComponent(lang || 'en')}`, {
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load substitute catalog');
    return data;
  }

  async resendInvoice(orderId) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/resend-invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to resend invoice');
    return data;
  }

  async sendDeliveryNote(fulfillmentId, date) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/fulfillments/${fulfillmentId}/delivery-note`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(date ? { date } : {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to send delivery note');
    return data;
  }

  async previewDeliveryNote(fulfillmentId, date) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/fulfillments/${fulfillmentId}/delivery-note-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(date ? { date } : {}),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to generate delivery note preview');
    }
    return res.blob();
  }

  // Admin Orders
  async getAdminOrders() {
    const res = await fetch(`${API_BASE_URL}/admin/orders`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch orders');
    return res.json();
  }

  async updateAdminOrder(id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update order');
    return res.json();
  }

  // Running ("ongoing") monthly orders
  // Which running orders this customer has open, keyed by billing address. Used
  // to present a monthly order as "add to the running order" when one exists.
  async getOngoingOrders(userId) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/ongoing-orders`, {
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load running orders');
    return data;
  }

  // Full state of one running order: items (including any refund lines), the
  // discount ledger, fulfillments and recomputed totals.
  async getOngoingOrder(orderId) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/ongoing/${orderId}`, {
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load running order');
    return data;
  }

  // Close a running order. Irreversible: this is what emails the customer once
  // and makes the order eligible for the monthly invoice.
  async completeOngoingOrder(orderId) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to complete running order');
    return json;
  }

  // Refund endpoints (Admin)
  async createRefund(orderId, data) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/refunds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Failed to create refund');
    return result;
  }

  async getOrderRefunds(orderId) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/refunds`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch refunds');
    return res.json();
  }

  async previewRefund(orderId, data) {
    const res = await fetch(`${API_BASE_URL}/admin/orders/${orderId}/refund-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to generate credit note preview');
    }
    return res.blob();
  }

  async retryRefund(refundId) {
    const res = await fetch(`${API_BASE_URL}/admin/refunds/${refundId}/retry`, {
      method: 'POST',
      credentials: 'include'
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Failed to retry refund');
    return result;
  }

  // Inventory (Admin)
  async getInventorySummary() {
    const res = await fetch(`${API_BASE_URL}/admin/inventory/summary`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch inventory summary');
    return res.json();
  }

  async getInventoryProductions(variantSku) {
    const res = await fetch(`${API_BASE_URL}/admin/inventory/${encodeURIComponent(variantSku)}/productions`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch production rows');
    return res.json();
  }

  async createInventoryProduction(variantSku, payload) {
    const res = await fetch(`${API_BASE_URL}/admin/inventory/${encodeURIComponent(variantSku)}/productions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to create production row');
    }
    return data;
  }

  async updateInventoryProduction(batchId, payload) {
    const res = await fetch(`${API_BASE_URL}/admin/inventory/${encodeURIComponent(batchId)}/productions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to update production row');
    }
    return data;
  }

  async deleteInventoryProduction(batchId) {
    const res = await fetch(`${API_BASE_URL}/admin/inventory/${encodeURIComponent(batchId)}/productions`, {
      method: 'DELETE',
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'Failed to delete production row');
    }
    return data;
  }

  // User Orders
  async getUserOrders() {
    const res = await fetch(`${API_BASE_URL}/orders`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch user orders');
    return res.json();
  }

  // ─── Products (public) ────────────────────────────────────────────
  async getProducts(options = {}) {
    const { category, search, lang = 'en', include_hidden } = options;
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    if (include_hidden) params.append('include_hidden', 'true');
    params.append('lang', lang);

    const response = await fetch(`${API_BASE_URL}/products?${params}`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch products');
    return response.json();
  }

  async getFeaturedProducts() {
    const response = await fetch(`${API_BASE_URL}/products/featured`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch featured products');
    return response.json();
  }

  async getAdminFeatured() {
    const response = await fetch(`${API_BASE_URL}/admin/featured`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch featured products');
    return response.json();
  }

  async updateFeatured(codes) {
    const response = await fetch(`${API_BASE_URL}/admin/featured`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ codes })
    });
    if (!response.ok) throw new Error('Failed to update featured products');
    return response.json();
  }

  // Get single parent product (with all variants)
  async getParentProduct(productCode, lang = 'en') {
    const response = await fetch(`${API_BASE_URL}/products/${encodeURIComponent(productCode)}?lang=${lang}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      if (response.status === 404) throw new Error('Product not found');
      throw new Error('Failed to fetch product');
    }
    return response.json();
  }

  // Get single variant by SKU
  async getVariant(sku, lang = 'en') {
    const response = await fetch(`${API_BASE_URL}/variants/${encodeURIComponent(sku)}?lang=${lang}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      if (response.status === 404) throw new Error('Product not found');
      throw new Error('Failed to fetch product');
    }
    return response.json();
  }

  // Legacy alias — some components may still call getProduct
  async getProduct(codeOrSku, lang = 'en') {
    return this.getParentProduct(codeOrSku, lang);
  }

  // ─── Products (admin) ─────────────────────────────────────────────
  async getAdminProducts(options = {}) {
    const { lang = 'en' } = options;
    const res = await fetch(`${API_BASE_URL}/admin/products?lang=${lang}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch admin products');
    return res.json();
  }

  async addProduct(data) {
    const res = await fetch(`${API_BASE_URL}/admin/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to add product');
    return res.json();
  }

  async updateProduct(productCode, data) {
    const res = await fetch(`${API_BASE_URL}/admin/products/${encodeURIComponent(productCode)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update product');
    return res.json();
  }

  // ─── Variants (admin) ─────────────────────────────────────────────
  async addVariant(parentCode, data) {
    const res = await fetch(`${API_BASE_URL}/admin/products/${encodeURIComponent(parentCode)}/variants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to add variant');
    return res.json();
  }

  async updateVariant(variantSku, data) {
    const res = await fetch(`${API_BASE_URL}/admin/products/variants/${encodeURIComponent(variantSku)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update variant');
    return res.json();
  }

  async hideVariant(variantSku, isHidden = true) {
    const res = await fetch(`${API_BASE_URL}/admin/products/variants/${encodeURIComponent(variantSku)}/hide`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_hidden: !!isHidden })
    });
    if (!res.ok) throw new Error('Failed to update variant visibility');
    return res.json();
  }

  async getCustomerPrices() {
    const res = await fetch(`${API_BASE_URL}/admin/customer-prices`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch customer prices');
    return res.json();
  }

  // Active custom prices for one customer (take-order wizard pre-fill).
  async getAdminUserCustomerPrices(userId) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}/customer-prices`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch customer prices');
    return res.json();
  }

  async createCustomerPrice(data) {
    const res = await fetch(`${API_BASE_URL}/admin/customer-prices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create customer price');
    }
    return res.json();
  }

  async updateCustomerPrice(id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/customer-prices/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update customer price');
    return res.json();
  }

  async deleteCustomerPrice(id) {
    const res = await fetch(`${API_BASE_URL}/admin/customer-prices/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete customer price');
    return res.json();
  }

  async getOffers() {
    const res = await fetch(`${API_BASE_URL}/admin/offers`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch offers');
    return res.json();
  }
  async addOffer(data) {
    const res = await fetch(`${API_BASE_URL}/admin/offers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to add offer');
    }
    return res.json();
  }
  async updateOffer(id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/offers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to update offer');
    }
    return res.json();
  }
  async deleteOffer(id) {
    const res = await fetch(`${API_BASE_URL}/admin/offers/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete offer');
    return res.json();
  }

  async getCoupons() {
    const res = await fetch(`${API_BASE_URL}/admin/coupons`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch coupons');
    return res.json();
  }
  async addCoupon(data) {
    const res = await fetch(`${API_BASE_URL}/admin/coupons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to add coupon');
    }
    return res.json();
  }
  async updateCoupon(id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/coupons/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to update coupon');
    }
    return res.json();
  }
  async getCouponUsage(id) {
    const res = await fetch(`${API_BASE_URL}/admin/coupons/${id}/orders`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch coupon usage');
    return res.json();
  }
  async deleteCoupon(id) {
    const res = await fetch(`${API_BASE_URL}/admin/coupons/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete coupon');
    return res.json();
  }

  async applyCoupon(code) {
    const res = await fetch(`${API_BASE_URL}/cart/apply-coupon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code })
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || 'Failed to apply coupon');
      err.error_code = data.error_code;
      throw err;
    }
    return data;
  }

  // Pickup Locations (Admin)
  async getAdminPickupLocations() {
    const res = await fetch(`${API_BASE_URL}/admin/pickup-locations`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch pickup locations');
    return res.json();
  }
  async createPickupLocation(data) {
    const res = await fetch(`${API_BASE_URL}/admin/pickup-locations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create pickup location');
    return res.json();
  }
  async updatePickupLocation(id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/pickup-locations/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update pickup location');
    return res.json();
  }

  async getPickupBookings() {
    const res = await fetch(`${API_BASE_URL}/admin/pickup-bookings`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch pickup bookings');
    return res.json();
  }

  async getUpcomingHolidays() {
    const res = await fetch(`${API_BASE_URL}/holidays/upcoming`);
    if (!res.ok) throw new Error('Failed to fetch holidays');
    return res.json();
  }

  // Pickup Locations (Public)
  async getPublicPickupLocations() {
    const res = await fetch(`${API_BASE_URL}/checkout/pickup-locations`);
    if (!res.ok) throw new Error('Failed to fetch pickup locations');
    return res.json();
  }

  // Shipping Config (countries & cities) — Admin
  async getAdminShippingList(listKey) {
    const res = await fetch(`${API_BASE_URL}/admin/shipping/${listKey}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`Failed to fetch shipping ${listKey}s`);
    return res.json();
  }

  async addShippingItem(listKey, name) {
    const res = await fetch(`${API_BASE_URL}/admin/shipping/${listKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || `Failed to add shipping ${listKey}`);
    }
    return res.json();
  }

  async updateShippingItem(listKey, id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/shipping/${listKey}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || `Failed to update shipping ${listKey}`);
    }
    return res.json();
  }

  async deleteShippingItem(listKey, id) {
    const res = await fetch(`${API_BASE_URL}/admin/shipping/${listKey}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || `Failed to delete shipping ${listKey}`);
    }
    return res.json();
  }

  // Shipping Tiers — Admin (configurable cost tiers)
  async getShippingTiers() {
    const res = await fetch(`${API_BASE_URL}/admin/shipping-tiers`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch shipping tiers');
    return res.json();
  }

  async updateShippingTiers(tiers) {
    const res = await fetch(`${API_BASE_URL}/admin/shipping-tiers`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ tiers })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to update shipping tiers');
    }
    return res.json();
  }

  // Shipping Tiers — Public (shipping info page)
  async getPublicShippingTiers() {
    const res = await fetch(`${API_BASE_URL}/shipping/tiers`);
    if (!res.ok) throw new Error('Failed to fetch shipping tiers');
    return res.json();
  }

  // Shipping Config — Public
  async getShippingConfig(listKey) {
    const res = await fetch(`${API_BASE_URL}/shipping/config/${listKey}`);
    if (!res.ok) throw new Error(`Failed to fetch shipping ${listKey}s`);
    return res.json();
  }

  // Product Categories (Admin)
  async getAdminProductCategories() {
    const res = await fetch(`${API_BASE_URL}/admin/product-categories`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch product categories');
    return res.json();
  }
  async addProductCategory(data) {
    const res = await fetch(`${API_BASE_URL}/admin/product-categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to add product category');
    }
    return res.json();
  }
  async updateProductCategory(id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/product-categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to update product category');
    }
    return res.json();
  }
  async deleteProductCategory(id) {
    const res = await fetch(`${API_BASE_URL}/admin/product-categories/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to delete product category');
    }
    return res.json();
  }

  // Product Categories (Public)
  async getProductCategories() {
    const res = await fetch(`${API_BASE_URL}/products/categories`);
    if (!res.ok) throw new Error('Failed to fetch product categories');
    return res.json();
  }

  // Landing Pages (Admin & Public)
  async getLandingPages() {
    const res = await fetch(`${API_BASE_URL}/landing`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch landing pages');
    return res.json();
  }
  async createLandingPage(data) {
    const res = await fetch(`${API_BASE_URL}/landing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create landing page');
    return res.json();
  }
  async updateLandingPage(id, data) {
    const res = await fetch(`${API_BASE_URL}/landing/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update landing page');
    return res.json();
  }
  async deleteLandingPage(id) {
    const res = await fetch(`${API_BASE_URL}/landing/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete landing page');
    return res.json();
  }
  async getActiveLandingPage() {
    const res = await fetch(`${API_BASE_URL}/landing/active`);
    if (!res.ok) throw new Error('Failed to fetch active landing page');
    return res.json();
  }

  // Files
  async getFiles() {
    const res = await fetch(`${API_BASE_URL}/files`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to list files');
    return res.json();
  }

  async uploadFiles(formData) {
    const res = await fetch(`${API_BASE_URL}/files/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData // Note: no Content-Type header so the browser sets the multipart boundary automatically
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to upload files');
    }
    return res.json();
  }

  async deleteFile(id) {
    const res = await fetch(`${API_BASE_URL}/files/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete file');
    return res.json();
  }

  // Accounting / Bookkeeping
  async getAccountingDocuments(params = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    if (params.document_type) qs.append('document_type', params.document_type);
    if (params.is_approved) qs.append('is_approved', params.is_approved);
    if (params.search) qs.append('search', params.search);
    if (params.tag) qs.append('tag', params.tag);
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    const res = await fetch(`${API_BASE_URL}/accounting/documents?${qs}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch accounting documents');
    return res.json();
  }

  async getAccountingDocument(id) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch document');
    return res.json();
  }

  async uploadAccountingDocument(formData) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to upload document');
    return data;
  }

  async deleteAccountingDocument(id) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete document');
    return res.json();
  }

  async processAccountingDocument(id) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}/process`, {
      method: 'POST',
      credentials: 'include'
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to process document');
    return data;
  }

  async batchProcessAccountingDocuments(ids = []) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/batch-process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ids })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Batch processing failed');
    return data;
  }

  async replaceAccountingDocumentItems(id, items) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ items })
    });
    if (!res.ok) throw new Error('Failed to update items');
    return res.json();
  }

  async approveAccountingDocument(id, force = false) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ force })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to approve document');
    return data;
  }

  async rejectAccountingDocument(id, notes = '') {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ notes })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to reject document');
    return data;
  }

  async updateAccountingDocumentHeader(id, fields) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}/header`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(fields)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update document header');
    return data;
  }

  async updateAccountingDocumentType(id, documentType) {
    const res = await fetch(`${API_BASE_URL}/accounting/documents/${id}/type`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ document_type: documentType })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update document type');
    return data;
  }

  async getAccountingStats() {
    const res = await fetch(`${API_BASE_URL}/accounting/stats`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch stats');
    return res.json();
  }

  async getAccountingVatReport(quarter, year) {
    const res = await fetch(`${API_BASE_URL}/accounting/vat-report?quarter=${quarter}&year=${year}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch VAT report');
    return res.json();
  }

  async saveAccountingVatReport(quarter, year) {
    const res = await fetch(`${API_BASE_URL}/accounting/vat-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ quarter, year })
    });
    if (!res.ok) throw new Error('Failed to save VAT report');
    return res.json();
  }

  async updateAccountingVatOverrides(quarter, year, overrides) {
    const res = await fetch(`${API_BASE_URL}/accounting/vat-report/${quarter}/${year}/overrides`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(overrides)
    });
    if (!res.ok) throw new Error('Failed to update VAT overrides');
    return res.json();
  }

  async submitAccountingVatReport(quarter, year) {
    const res = await fetch(`${API_BASE_URL}/accounting/vat-report/${quarter}/${year}/submit`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to submit VAT report');
    return res.json();
  }

  async reopenAccountingVatReport(quarter, year) {
    const res = await fetch(`${API_BASE_URL}/accounting/vat-report/${quarter}/${year}/reopen`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to reopen VAT report');
    return res.json();
  }

  async getFileBlob(fileId) {
    const res = await fetch(`${API_BASE_URL}/files/${fileId}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to load file');
    return res.blob();
  }

  // Receipt linking
  async uploadItemReceipt(itemId, file) {
    const fd = new FormData();
    fd.append('receipt', file);
    const res = await fetch(`${API_BASE_URL}/accounting/items/${itemId}/receipt`, {
      method: 'POST',
      credentials: 'include',
      body: fd
    });
    if (!res.ok) throw new Error('Failed to upload receipt');
    return res.json();
  }

  async removeItemReceipt(itemId) {
    const res = await fetch(`${API_BASE_URL}/accounting/items/${itemId}/receipt`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to remove receipt');
    return res.json();
  }

  async getReceiptMatchSuggestions(docId) {
    const res = await fetch(`${API_BASE_URL}/accounting/receipts/${docId}/match`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to get suggestions');
    return res.json();
  }

  // Languages
  async getLanguages() {
    const res = await fetch(`${API_BASE_URL}/languages`);
    if (!res.ok) throw new Error('Failed to load languages');
    return res.json();
  }

  // Holidays (Admin)
  async getAdminHolidays() {
    const res = await fetch(`${API_BASE_URL}/holidays`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch holidays');
    return res.json();
  }

  async createAdminHoliday(data) {
    const res = await fetch(`${API_BASE_URL}/holidays`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create holiday');
    }
    return res.json();
  }

  async updateAdminHoliday(id, data) {
    const res = await fetch(`${API_BASE_URL}/holidays/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to update holiday');
    }
    return res.json();
  }

  async deleteAdminHoliday(id) {
    const res = await fetch(`${API_BASE_URL}/holidays/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete holiday');
    return res.json();
  }

  // Holidays (Public)
  async getCurrentHoliday() {
    const res = await fetch(`${API_BASE_URL}/holidays/current`);
    if (!res.ok) throw new Error('Failed to fetch current holiday');
    return res.json();
  }

  async updateItemTags(itemId, tags) {
    const res = await fetch(`${API_BASE_URL}/accounting/items/${itemId}/tags`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ tags })
    });
    if (!res.ok) throw new Error('Failed to update item tags');
    return res.json();
  }

  // Email templates (admin)
  async getEmailTemplates() {
    const res = await fetch(`${API_BASE_URL}/admin/email-templates`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to load email templates');
    return res.json();
  }

  async getEmailTemplate(id) {
    const res = await fetch(`${API_BASE_URL}/admin/email-templates/${id}`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to load email template');
    return res.json();
  }

  async sendTestEmail(data) {
    const res = await fetch(`${API_BASE_URL}/admin/email-templates/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to send test email');
    }
    return res.json();
  }

  async createEmailTemplate(data) {
    const res = await fetch(`${API_BASE_URL}/admin/email-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create email template');
    }
    return res.json();
  }

  async updateEmailTemplate(id, data) {
    const res = await fetch(`${API_BASE_URL}/admin/email-templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to update email template');
    }
    return res.json();
  }

  async deleteEmailTemplate(id) {
    const res = await fetch(`${API_BASE_URL}/admin/email-templates/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete email template');
    return res.json();
  }
}

const apiService = new ApiService();
export default apiService;